import React from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import configuration from 'configuration';

export const CountriesInMap = [
  'Albania',
  'Algeria',
  'Andorra',
  'Armenia',
  'Austria',
  'Belarus',
  'Belgium',
  'Bosnia Herzegovina',
  'Bulgaria',
  'Channel Islands',
  'Croatia',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Faroe Islands',
  'Finland',
  'France',
  'Georgia',
  'Germany',
  'Gibraltar',
  'Greece',
  'Hungary',
  'Iceland',
  'Iraq',
  'Ireland',
  'Isle of Man',
  'Israel',
  'Italy',
  'Jordan',
  'Kosovo',
  'Latvia',
  'Lebanon',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Moldova',
  'Monaco',
  'Montenegro',
  'Morocco',
  'Netherlands',
  'North Macedonia',
  'Norway',
  'Palestina',
  'Poland',
  'Portugal',
  'Republic of Cyprus',
  'Romania',
  'Russia',
  'San Marino',
  'Saudi Arabia',
  'Serbia',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'Switzerland',
  'Syria',
  'Tunisia',
  'Türkiye',
  'Ukraine',
  'United Kingdom',
  'Vatican',
] as const;

interface CountryIndex {
  year: number;
  rank: number | string;
  total: number;
}

export interface CountryInfo {
  code: string;
  name: string;
  localName: string;
  euMember?: number | string;
  population: number;
  capital: string;
  languages: string[];
  currency: string;
  indices: Record<string, number>;
}

const MapOverlay = ({
  path,
  country,
  leftSide,
}: {
  path: string;
  country: string;
  leftSide: boolean;
}) => {
  const {t} = useTranslation();

  const countryPath =
    (CountriesInMap.includes(country as any) ? 'countries.' : 'extraCountries.') + country;

  const countryInfo: any = t(configuration.path2langKey(path, countryPath), {
    lng: 'de',
    returnObjects: true,
    defaultValue: {},
  });
  const info: CountryInfo = {
    code: '?',
    localName: '?',
    population: 0,
    languages: [],
    ...countryInfo,
  };

  const tp = (key: string) => t(configuration.path2langKey(path, 'general.' + key));
  const notAvailable = tp('notAvailable');

  info.name = t(configuration.path2langKey(path, countryPath + '.name'), {
    defaultValue: '?',
  });
  info.capital = t(configuration.path2langKey(path, countryPath + '.capital'), {
    defaultValue: '?',
  });
  info.currency = t(configuration.path2langKey(path, countryPath + '.currency'), {
    defaultValue: '?',
  });

  if (typeof info.euMember === 'string') {
    info.euMember =
      t(configuration.path2langKey(path, countryPath + '.euMember'), {
        defaultValue: '?',
      }) ?? '?';
  } else {
    info.euMember = info.euMember ? tp('since') + ' ' + String(info.euMember) : tp('no');
  }

  const p = parseInt(String(info.population).replaceAll(/[.,]/g, ''), 10);
  info.population = isNaN(p) ? 0 : p;

  if (!Array.isArray(info.languages)) info.languages = [];
  if (!info.indices) info.indices = {};

  const indices: Record<string, CountryIndex> = t(
    configuration.path2langKey(path, 'general.indices'),
    {
      lng: 'de',
      returnObjects: true,
      defaultValue: {},
    },
  );
  for (const [name, index] of Object.entries(indices)) {
    index.rank = parseInt(String(info.indices[name]) || '0', 10);
    if (isNaN(index.rank) || index.rank <= 0) index.rank = notAvailable;
  }

  return (
    <Container style={leftSide ? {right: 'var(--margin)'} : {left: 'var(--margin)'}}>
      <CountryTable>
        <FlagRow code={info.code} />
        <CountryRow left={info.name} right={info.localName} headline={true} />
        <CountryRow left={tp('euMember')} right={info.euMember} />
        <CountryRow
          left={tp('population')}
          right={info.population ? fmtNumber(info.population, tp('millions')) : '?'}
        />
        <CountryRow left={tp('capital')} right={info.capital} />
        <CountryRow
          left={tp('languages')}
          right={
            info.languages.length ? info.languages.map(l => tp('langCodes.' + l)).join(', ') : '?'
          }
        />
        <CountryRow left={tp('currency')} right={info.currency} />
      </CountryTable>

      <div className="index-container">
        <IndexScaleContainer>
          {Object.entries(indices).map(([name, item]) => (
            <IndexScale key={name} title={tp('indices.' + name + '.name')} {...item} />
          ))}
        </IndexScaleContainer>
      </div>
    </Container>
  );
};
export default MapOverlay;

const Container = styled.div`
  position: absolute;
  top: var(--margin);
  width: 320px;
  padding: var(--margin) var(--margin) 0 var(--margin);
  background: rgba(240, 240, 240, 0.8);
  font-weight: 500;
  pointer-events: none;

  .ratio-narrow & {
    top: 25%;
  }

  & table {
    border-collapse: collapse;
    margin-bottom: var(--margin);

    & tr {
      vertical-align: top;
    }

    & td {
      padding-bottom: var(--margin);

      &.headline {
        text-transform: uppercase;
        font-weight: 700;
      }
      &.right-row {
        padding-left: var(--margin);
      }
    }

    & tr.disabled td {
      color: var(--color-gray-50);
    }

    & span.fi {
      width: 3em;
      height: 2.25em;
    }
  }

  & .index-container {
    padding-bottom: var(--margin);

    @media screen and (max-height: 670px) {
      display: none;
    }
  }
`;

const CountryTable = ({children}: {children?: React.ReactNode}) => (
  <table>
    <tbody>{children}</tbody>
  </table>
);

const FlagRow = ({code}: {code: string}) => (
  <tr>
    <td>
      <span className={'fi fi-' + code.toLowerCase()}></span>
    </td>
  </tr>
);

const CountryRow = ({
  className,
  left,
  right,
  headline,
}: {
  className?: string;
  left: string | null;
  right?: string | number | null;
  headline?: true;
}) => (
  <tr className={className}>
    <td className={headline && 'headline'}>{(left ?? '') + (headline ?? !right ? '' : ':')}</td>
    <td className="right-row">{right}</td>
  </tr>
);

const IndexScale = ({
  title,
  year,
  rank,
  total,
}: {
  title: string;
  year: number;
  rank: number | string;
  total: number;
}) => {
  const na = typeof rank === 'string';
  const p = na ? 0 : 100 - (100 * rank) / total;
  const tts = p < 50 ? {left: String(p) + '%'} : {right: String(100 - p) + '%'};

  return (
    <ScaleContainer className={na ? 'disabled' : undefined}>
      <label>{title + (na ? '' : ' (' + String(year) + ')')}</label>
      <Scale>
        {na ? (
          <DisabledText>{rank}</DisabledText>
        ) : (
          <>
            <Thumb style={{left: String(p) + '%'}} />
            <ThumbText style={tts}>{[rank, total].join('/')}</ThumbText>
          </>
        )}
      </Scale>
    </ScaleContainer>
  );
};

const IndexScaleContainer = styled.div`
  margin-bottom: var(--margin);
`;

const ScaleContainer = styled.div`
  margin-top: var(--margin);

  & label {
    display: block;
    margin-bottom: 4px;
  }

  &.disabled label {
    color: var(--color-gray-50);
  }
`;

const Scale = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  border: 1px solid var(--color-gray-50);
  border-radius: 8px;
  background: linear-gradient(90deg, var(--color-red), var(--color-yellow), var(--color-green));

  .disabled & {
    background: var(--color-border);
  }
`;

const Thumb = styled.div`
  position: absolute;
  top: -4px;
  width: 4px;
  height: 32px;
  background: var(--color-text);
`;

const ThumbText = styled.div`
  position: absolute;
  top: -4px;
  height: 32px;
  line-height: 32px;
  font-size: 14px;
  margin-left: 10px;
  margin-right: 6px;
`;

const DisabledText = styled.div`
  position: absolute;
  top: -4px;
  left: 0;
  height: 32px;
  line-height: 32px;
  font-size: 14px;
  color: var(--color-gray-50);
  margin-left: 10px;
`;

const fmtNumber = (n: number, mio: string) =>
  n < 1000000
    ? n.toLocaleString('de')
    : String(Math.round(n / 100000) / 10).replace('.', ',') + ' ' + mio;
